import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Stack,
  Divider,
  Alert,
  IconButton,
  AppBar,
  Toolbar,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  AccountTree as NetworkIcon,
} from '@mui/icons-material'
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabaseClient'

function ProfilePage() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const [editing, setEditing] = useState(false)
  const [fullName, setFullName] = useState(
    user?.user_metadata?.full_name || user?.user_metadata?.name || ''
  )
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  // Theme
  const [themeMode, setThemeMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode')
    return savedMode || 'light'
  })
  const darkMode = themeMode === 'dark'

  const theme = useMemo(() => createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: '#3b82f6' },
      secondary: { main: '#8b5cf6' },
      background: {
        default: darkMode ? '#0f172a' : '#ffffff',
        paper: darkMode ? '#1e293b' : '#ffffff',
      },
    },
  }), [darkMode])

  const toggleTheme = () => {
    setThemeMode(prevMode => {
      const newMode = prevMode === 'light' ? 'dark' : 'light'
      localStorage.setItem('themeMode', newMode)
      return newMode
    })
  }

  const handleUpdateProfile = async () => {
    setError('')
    setSuccess('')
    setLoading(true)

    try {
      const updates = {}

      if (fullName !== user?.user_metadata?.full_name) {
        updates.data = { full_name: fullName }
      }

      if (newPassword) {
        if (newPassword !== confirmPassword) {
          setError('Passwords do not match')
          setLoading(false)
          return
        }
        if (newPassword.length < 6) {
          setError('Password must be at least 6 characters')
          setLoading(false)
          return
        }
        updates.password = newPassword
      }

      const { error } = await supabase.auth.updateUser(updates)

      if (error) throw error

      setSuccess('Profile updated successfully!')
      setEditing(false)
      setNewPassword('')
      setConfirmPassword('')

      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.message || 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleBackClick = () => {
    const referrer = localStorage.getItem('profileReferrer') || '/app'
    localStorage.removeItem('profileReferrer') 
    navigate(referrer)
  }

  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete your account? This action cannot be undone.'
      )
    ) {
      try {
        alert('Please contact support to delete your account.')
      } catch (err) {
        setError('Failed to delete account')
      }
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header - Exact copy from CommunityLayout */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            background: darkMode
              ? 'linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.85) 100%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.85) 100%)',
            backdropFilter: 'blur(20px)',
            borderBottom: 1,
            borderColor: darkMode ? '#334155' : '#e2e8f0',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ px: 0 }}>
              {/* Left: Back Button + Logo */}
              <IconButton
                onClick={handleBackClick}
                sx={{
                  mr: 2,
                  color: darkMode ? '#fff' : '#0f172a',
                }}
              >
                <ArrowBackIcon />
              </IconButton>

              <Box
                onClick={() => navigate('/')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mr: 4,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                  }}
                >
                  <NetworkIcon sx={{ fontSize: 20, color: 'white' }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: '1.1rem',
                  }}
                >
                  Profile Settings
                </Typography>
              </Box>

              <Box sx={{ flexGrow: 1 }} />

              {/* Right: Theme Toggle */}
              <IconButton
                onClick={toggleTheme}
                sx={{
                  mr: 2,
                  bgcolor: darkMode ? '#1e293b' : '#f1f5f9',
                  color: darkMode ? '#fbbf24' : '#3b82f6',
                  transition: 'all 0.5s ease',
                  '&:hover': {
                    transform: 'rotate(180deg)',
                    bgcolor: darkMode ? '#334155' : '#e2e8f0',
                  },
                }}
              >
                {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>

        {/* Content */}
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                src={user?.user_metadata?.avatar_url}
                sx={{
                  width: 80,
                  height: 80,
                  mr: 3,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  fontSize: '2rem',
                  fontWeight: 700,
                }}
              >
                {fullName?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary' }}>
                  {fullName || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              {!editing && (
                <IconButton onClick={() => setEditing(true)} color="primary">
                  <EditIcon />
                </IconButton>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Alerts */}
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Profile Info */}
            <Stack spacing={3}>
              <TextField
                label="Full Name"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                disabled={!editing}
                fullWidth
              />

              <TextField
                label="Email"
                value={user?.email}
                disabled
                fullWidth
                helperText="Email cannot be changed"
              />

              {editing && (
                <>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Change Password (Optional)
                    </Typography>
                  </Divider>

                  <TextField
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    fullWidth
                    helperText="Leave blank to keep current password"
                  />

                  <TextField
                    label="Confirm New Password"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    fullWidth
                    disabled={!newPassword}
                  />
                </>
              )}

              {/* Action Buttons */}
              {editing ? (
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleUpdateProfile}
                    disabled={loading}
                    sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    }}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditing(false)
                      setFullName(user?.user_metadata?.full_name || '')
                      setNewPassword('')
                      setConfirmPassword('')
                      setError('')
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteAccount}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Delete Account
                  </Button>
                </Stack>
              )}
            </Stack>

            <Divider sx={{ my: 4 }} />

            {/* Account Info */}
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
              Account Information
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Account Created
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {new Date(user?.created_at).toLocaleDateString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Email Verified
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {user?.email_confirmed_at ? 'Yes' : 'No'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Sign-in Method
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {user?.app_metadata?.provider || 'Email'}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  )
}

export default ProfilePage